import { ReactNode, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { EmptyState } from '@new/common/ui/EmptyState/EmptyState';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortableTableColumn<T = any> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  render?: (value: any, row: T) => ReactNode;
  className?: string;
}

interface SortableTableProps<T = any> {
  data: T[];
  columns: SortableTableColumn<T>[];
  getRowKey: (row: T) => string | number;
  emptyMessage?: string;
  emptyDescription?: string;
  onRowClick?: (row: T) => void;
  isRowSelected?: (row: T) => boolean;
  getRowClassName?: (row: T) => string;
  sortKey?: string;
  sortDirection?: SortDirection;
  onSortChange?: (key: string, direction: SortDirection) => void;
}

function nextSortDirection(current: SortState, key: string): SortDirection {
  if (current.key !== key) return 'asc';
  if (current.direction === 'asc') return 'desc';
  if (current.direction === 'desc') return null;
  return 'asc';
}

interface SortState {
  key: string;
  direction: SortDirection;
}

const SORT_ICON_SIZE = 14;

function getSortedData<T>(data: T[], sort: SortState): T[] {
  if (!sort.direction || !sort.key) return data;

  return [...data].sort((a, b) => {
    const aVal = (a as any)[sort.key];
    const bVal = (b as any)[sort.key];

    if (aVal === bVal) return 0;

    const comparison = aVal < bVal ? -1 : 1;
    return sort.direction === 'asc' ? comparison : -comparison;
  });
}

function SortIcon({ direction }: { direction: SortDirection }) {
  if (direction === 'asc') return <ChevronUp size={SORT_ICON_SIZE} />;
  if (direction === 'desc') return <ChevronDown size={SORT_ICON_SIZE} />;
  return <ChevronsUpDown size={SORT_ICON_SIZE} className="text-muted-foreground/50" />;
}

export function SortableTable<T = any>({
  data,
  columns,
  getRowKey,
  emptyMessage = 'No data available',
  emptyDescription = 'There are no items to display at this time.',
  onRowClick,
  isRowSelected,
  getRowClassName,
  sortKey,
  sortDirection,
  onSortChange,
}: SortableTableProps<T>) {
  const isControlled = onSortChange !== undefined;

  const [internalSort, setInternalSort] = useState<SortState>({ key: '', direction: null });

  const sort: SortState = isControlled
    ? { key: sortKey ?? '', direction: sortDirection ?? null }
    : internalSort;

  const onHeaderClick = (key: string) => {
    const direction = nextSortDirection(sort, key);
    if (isControlled) {
      onSortChange(key, direction);
      return;
    }
    setInternalSort({ key, direction });
  };

  const sortedData = isControlled ? data : getSortedData(data, sort);

  if (data.length === 0) {
    return <EmptyState message={emptyMessage} description={emptyDescription} />;
  }

  return (
    <div className="flow-root">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div className="overflow-hidden shadow-sm outline outline-1 outline-black/5 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key as string}
                      scope="col"
                      className={`py-3.5 px-3 text-left text-sm font-semibold text-[var(--neutral-black-NB1000)] first:pl-4 first:sm:pl-6 last:pr-4 last:sm:pr-6 ${col.sortable ? 'cursor-pointer select-none' : ''} ${col.className ?? ''}`}
                      onClick={col.sortable ? () => onHeaderClick(col.key as string) : undefined}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.header}
                        {col.sortable && (
                          <SortIcon direction={sort.key === col.key ? sort.direction : null} />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {sortedData.map((row) => (
                  <tr
                    key={getRowKey(row)}
                    className={`transition-colors ${onRowClick ? 'cursor-pointer' : ''} ${isRowSelected?.(row) ? 'bg-[color-mix(in_srgb,var(--primary-O200)_40%,transparent)]' : 'hover:bg-gray-50'} ${getRowClassName?.(row) ?? ''}`}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key as string}
                        className={`py-4 px-3 text-sm whitespace-nowrap text-[var(--neutral-black-NB800)] first:pl-4 first:font-medium first:text-[var(--neutral-black-NB1000)] first:sm:pl-6 last:pr-4 last:sm:pr-6 ${col.className ?? ''}`}
                      >
                        {col.render
                          ? col.render((row as any)[col.key], row)
                          : String((row as any)[col.key] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
